import { usePostHog } from "@posthog/react";
import { useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import type { AnyAction, Reducer } from "redux";
import useAuth from "./accessToken/hooks/useAuth";

export const useAccountId = () => useParams<{ accountId: string }>().accountId;

const AccountIdChangedActionType = "@@accountIdChanged" as const;

/**
 * This action will be dispatched whenever the :accountId parameter in path changes
 */
type AccountIdChangedAction = {
    type: typeof AccountIdChangedActionType;
    accountId: string;
};

const isAccountIdChangedAction = (
    action: AnyAction,
): action is AccountIdChangedAction =>
    action.type === AccountIdChangedActionType;

export const createAccountIdChangedCaseReducer =
    <S>(reducer: Reducer<S, AccountIdChangedAction>) =>
    (state: S, action: AnyAction) => {
        if (isAccountIdChangedAction(action)) {
            return reducer(state, action);
        }
        return state;
    };

/**
 * @note Only for use in root App.tsx. There can only be one
 */
export const AccountIdChangedDispatcher = () => {
    const accountId = useAccountId();
    const dispatch = useDispatch();
    const posthog = usePostHog();
    const isInspecting = useAuth((state) => !!state.exchangeTokenPartnerId);

    useLayoutEffect(() => {
        dispatch({ type: AccountIdChangedActionType, accountId });
        // We use cookieless mode so we should not identify any user.
        // Instead, we identify the current account context using `accountId`,
        // or `${accountId}-inspect` in inspect mode, while still sending the
        // raw merchant account id as the `account_id` property.
        const posthogId = isInspecting ? `${accountId}-inspect` : accountId;
        posthog.identify(posthogId, {
            account_id: accountId,
            is_inspecting: isInspecting,
        });
    }, [dispatch, accountId, posthog, isInspecting]);

    return null;
};
